import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { motion } from 'framer-motion';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import { DeviceFrame, SignalsScreen } from '@/components/AppScreens';
import { SectionHeading } from '@/components/sections/SectionHeading';
import { PricingCards } from '@/components/sections/PricingCards';
import { CtaBanner } from '@/components/sections/CtaBanner';

export default function Followers() {
  const { t } = useTranslation();
  const benefits = t('followers.benefits', { returnObjects: true });
  const onboarding = t('followers.onboarding', { returnObjects: true });

  return (
    <>
      <Seo title={t('followers.seoTitle')} description={t('followers.seoDescription')} />

      <section className="relative overflow-hidden px-6 pb-16 pt-32 lg:px-8 sm:pt-40">
        <div className="pointer-events-none absolute right-0 top-24 -z-10 h-96 w-96 rounded-full bg-brand/10 blur-[120px]" />
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand">
              <Icons.Users className="h-3.5 w-3.5" /> {t('followers.badge')}
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t('followers.title1')} <span className="text-brand glow-text">{t('followers.title2')}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-lg text-lg text-white/60 lg:mx-0">
              {t('followers.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start">
              <Link to="/pricing" className="btn-primary">{t('common.startFreeTrial')} <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/providers" className="btn-secondary">{t('followers.ctaProvider')}</Link>
            </div>
          </div>
          <Reveal delay={0.15} className="perspective flex justify-center">
            <DeviceFrame tilt width="w-[270px]"><SignalsScreen /></DeviceFrame>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-24 lg:px-8">
        <SectionHeading eyebrow={t('followers.benefitsEyebrow')} title={t('followers.benefitsTitle')} />
        <Stagger className="mx-auto mt-14 grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b) => {
            const Icon = Icons[b.icon] || Icons.Check;
            return (
              <StaggerItem key={b.title}>
                <motion.div whileHover={{ y: -6 }} className="group h-full rounded-2xl border border-white/10 bg-surface/70 p-7 transition-colors hover:border-brand/30">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#001826] text-brand ring-1 ring-brand/20 shadow-[0_0_24px_rgba(23,253,253,0.2)] transition-transform duration-300 group-hover:scale-110">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-white/60">{b.desc}</p>
                </motion.div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <span className="eyebrow">{t('followers.onboardingEyebrow')}</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                {t('followers.onboardingTitle')}
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
                {t('followers.onboardingSubtitle')}
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute bottom-4 left-[19px] top-3 w-px bg-gradient-to-b from-brand/50 via-brand/20 to-transparent" />
              {onboarding.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div className="relative flex items-start gap-5 pb-5 last:pb-0">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand font-display font-bold text-black shadow-[0_0_20px_rgba(23,253,253,0.4)] ring-4 ring-[#00121D]">{i + 1}</div>
                    <div className="flex-1 rounded-2xl border border-white/10 bg-surface/70 p-5 transition-colors hover:border-brand/30">
                      <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                      <p className="mt-1 text-sm text-white/60">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-24 lg:px-8">
        <SectionHeading eyebrow={t('followers.pricingEyebrow')} title={t('followers.pricingTitle')} subtitle={t('followers.pricingSubtitle')} />
        <div className="mx-auto mt-14 max-w-6xl"><PricingCards audience="follower" /></div>
      </section>

      <CtaBanner />
    </>
  );
}
